import { useState } from 'react'
import { X, Plus, Trash2 } from 'lucide-react'

function buildForm(org) {
  if (!org) return { name: '', location: '', description: '' }
  return { name: org.name, location: org.location, description: org.description || '' }
}

function buildBranches(existingBranches) {
  return existingBranches.map((b) => ({ id: b.id, name: b.name, location: b.location }))
}

export default function OrgModal({ org, existingBranches, onSave, onClose }) {
  const [form, setForm] = useState(() => buildForm(org))
  const [formBranches, setFormBranches] = useState(() => buildBranches(existingBranches))
  const [error, setError] = useState('')

  const isEditing = !!org

  function setField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }))
  }

  function addBranch() {
    setFormBranches((prev) => [...prev, { id: null, name: '', location: '' }])
  }

  function removeBranch(index) {
    setFormBranches((prev) => prev.filter((_, i) => i !== index))
  }

  function updateBranch(index, field, value) {
    setFormBranches((prev) =>
      prev.map((b, i) => (i === index ? { ...b, [field]: value } : b))
    )
  }

  function handleSubmit() {
    if (!form.name.trim()) { setError('Organization name is required.'); return }
    if (!form.location.trim()) { setError('Location is required.'); return }
    for (const b of formBranches) {
      if (!b.name.trim()) { setError('All branch names are required.'); return }
    }
    setError('')
    onSave(form, formBranches)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-[70] p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-xl max-h-[95vh] sm:max-h-[85vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-base sm:text-lg font-bold text-gray-800">
              {isEditing ? 'Edit Organization' : 'Add Organization'}
            </h2>
            {isEditing && <p className="text-xs text-gray-500 mt-0.5">{org.name}</p>}
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-lg transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-6">

          {/* Org details */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-gray-700 pb-1 border-b border-gray-100">
              Organization Details
            </h3>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Organization Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                placeholder="e.g. Delta Petroleum Ltd"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Location <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setField('location', e.target.value)}
                placeholder="e.g. Lagos, Nigeria"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
                placeholder="Brief description of the organization"
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none resize-none"
              />
            </div>
          </div>

          {/* Branches */}
          <div className="space-y-3">
            <div className="flex items-center justify-between pb-1 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700">
                Branches
                <span className="ml-1 text-xs font-normal text-gray-400">({formBranches.length})</span>
              </h3>
              <button
                onClick={addBranch}
                className="flex items-center space-x-1 text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1.5 rounded-lg transition"
              >
                <Plus className="w-3 h-3" />
                <span>Add Branch</span>
              </button>
            </div>

            {formBranches.length === 0 ? (
              <p className="text-xs text-gray-400 text-center py-4 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                No branches added. You can add them now or configure them later in the Branches tab.
              </p>
            ) : (
              <div className="space-y-3">
                {formBranches.map((branch, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-gray-600">Branch {index + 1}</span>
                      <button
                        onClick={() => removeBranch(index)}
                        className="p-1 hover:bg-red-100 text-red-400 hover:text-red-600 rounded transition"
                        title="Remove branch"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={branch.name}
                          onChange={(e) => updateBranch(index, 'name', e.target.value)}
                          placeholder="e.g. Ikeja Branch"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">Location</label>
                        <input
                          type="text"
                          value={branch.location}
                          onChange={(e) => updateBranch(index, 'location', e.target.value)}
                          placeholder="e.g. Ikeja, Lagos"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl flex flex-col gap-3">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2">{error}</p>
          )}
          <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleSubmit}
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 rounded-lg transition"
          >
            {isEditing ? 'Update Organization' : 'Add Organization'}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2.5 rounded-lg transition"
          >
            Cancel
          </button>
          </div>
        </div>
      </div>
    </div>
  )
}
